import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, Upload, Camera, X as XIcon, Receipt } from 'lucide-react';
import { api, fileUrl } from '../lib/api';

/**
 * Manages a list of vendor bills attached to a single doc (OnSitePurchase
 * or GRN). Each bill carries vendor name, bill #, bill date, plus an
 * optional uploaded receipt image stored in GridFS.
 *
 * The parent component owns the array (controlled component); this view
 * just renders + edits + uploads. Per-row bill assignment (which spare
 * belongs to which bill) lives on the cart-row dropdown elsewhere.
 *
 * OCR auto-fill: when a file is uploaded, /api/ai/ocr-invoice is called
 * with the data URL — vendor name + bill # + bill date land in the form
 * automatically. Manual edit always wins.
 */

export interface BillSlot {
  vendorName: string;
  vendorBillNo: string;
  vendorBillDate: string;        // yyyy-mm-dd
  attachmentId?: string;          // GridFS file id once uploaded
  filename?: string;
  contentType?: string;
}

export function emptyBillSlot(): BillSlot {
  return { vendorName: '', vendorBillNo: '', vendorBillDate: '' };
}

export function BillsManager({
  bills,
  onChange,
  totalAmount,
}: {
  bills: BillSlot[];
  onChange: (bills: BillSlot[]) => void;
  totalAmount?: number;
}) {
  const addBill = () => onChange([...bills, emptyBillSlot()]);
  const updateBill = (idx: number, patch: Partial<BillSlot>) =>
    onChange(bills.map((b, i) => i === idx ? { ...b, ...patch } : b));
  const removeBill = (idx: number) => onChange(bills.filter((_, i) => i !== idx));

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1.5">
            <Receipt size={12} /> Vendor bills ({bills.length})
          </div>
          {totalAmount !== undefined && (
            <div className="text-[10px] text-slate-500">Cart total ₹{Math.round(totalAmount).toLocaleString('en-IN')} — bills can split it across vendors.</div>
          )}
        </div>
        <button
          type="button"
          onClick={addBill}
          className="px-2 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium flex items-center gap-1"
        >
          <Plus size={12} /> Add bill
        </button>
      </div>

      {bills.length === 0 ? (
        <div className="text-xs text-slate-500 italic text-center py-3 border border-dashed border-slate-200 rounded">
          No bills attached. Click <b>+ Add bill</b> to start.
        </div>
      ) : (
        <div className="space-y-2">
          {bills.map((b, i) => (
            <BillRow
              key={i}
              index={i}
              bill={b}
              onChange={(patch) => updateBill(i, patch)}
              onRemove={() => removeBill(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface OcrInvoice {
  vendorName: string; billNo: string; billDate: string;
  totalAmount: number;
}

function BillRow({ index, bill, onChange, onRemove }: {
  index: number; bill: BillSlot;
  onChange: (patch: Partial<BillSlot>) => void;
  onRemove: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  async function uploadAndOcr(file: File) {
    setBusy(true);
    try {
      // 1) Upload to GridFS so the dashboard can render it later. Bills are
      // stored as PDF (the server converts an image), so trust the response's
      // contentType/filename — not the original file's image type.
      const up = await api.upload<{ id: string; filename: string; contentType?: string }>('/api/files/upload', file, { purpose: 'bill' });
      onChange({ attachmentId: up.id, filename: up.filename, contentType: up.contentType || file.type });

      // 2) Try OCR — if it fails, the upload still stands; user types fields.
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });
      try {
        const ocr = await api.post<OcrInvoice | null>('/api/ai/ocr-invoice', { imageDataUrl: dataUrl });
        if (ocr) {
          const patch: Partial<BillSlot> = {};
          if (ocr.vendorName && !bill.vendorName) patch.vendorName = ocr.vendorName;
          if (ocr.billNo && !bill.vendorBillNo) patch.vendorBillNo = ocr.billNo;
          if (ocr.billDate && !bill.vendorBillDate) patch.vendorBillDate = ocr.billDate;
          if (Object.keys(patch).length) onChange(patch);
          toast.success(`Bill ${index + 1}: parsed ${ocr.vendorName || ocr.billNo || 'OK'}`);
        }
      } catch { /* OCR is best-effort */ }
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) await uploadAndOcr(f);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function openCamera() {
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 0);
    } catch {
      toast.error('Camera blocked — use the file picker instead.');
      setCameraOpen(false);
    }
  }

  function closeCamera() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  }

  function captureFrame() {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `bill-${index + 1}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      closeCamera();
      await uploadAndOcr(file);
    }, 'image/jpeg', 0.85);
  }

  const previewUrl = bill.attachmentId ? fileUrl(bill.attachmentId) : null;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-mono text-slate-500">Bill #{index + 1}</div>
        <button type="button" onClick={onRemove} className="text-slate-400 hover:text-red-600">
          <Trash2 size={12} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase text-slate-500 mb-0.5">Vendor</div>
          <input
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            value={bill.vendorName}
            onChange={(e) => onChange({ vendorName: e.target.value })}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500 mb-0.5">Bill #</div>
          <input
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            value={bill.vendorBillNo}
            onChange={(e) => onChange({ vendorBillNo: e.target.value })}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500 mb-0.5">Bill date</div>
          <input
            type="date"
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            value={bill.vendorBillDate}
            onChange={(e) => onChange({ vendorBillDate: e.target.value })}
          />
        </div>
        <div className="flex items-end gap-1">
          <button
            type="button"
            onClick={openCamera}
            disabled={busy}
            className="flex-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-xs flex items-center justify-center gap-1"
          >
            <Camera size={12} /> Cam
          </button>
          <label className="flex-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-xs flex items-center justify-center gap-1 cursor-pointer">
            <Upload size={12} /> {busy ? '…' : 'File'}
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={onPickFile}
              disabled={busy}
            />
          </label>
        </div>
      </div>

      {previewUrl && (
        <div className="rounded-md overflow-hidden border border-slate-200 bg-white">
          {(bill.contentType || '').includes('pdf') ? (
            <a href={previewUrl} target="_blank" rel="noopener" className="block px-2 py-3 text-xs text-brand-700 hover:underline truncate">
              📄 {bill.filename || 'bill.pdf'}
            </a>
          ) : (
            <a href={previewUrl} target="_blank" rel="noopener" className="block">
              <img src={previewUrl} alt={`bill-${index + 1}`} className="w-full max-h-32 object-contain" />
            </a>
          )}
        </div>
      )}

      {cameraOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={closeCamera}>
          <div className="bg-white rounded-lg max-w-md w-full p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Capture bill #{index + 1}</div>
              <button onClick={closeCamera} className="text-slate-500 hover:text-slate-800"><XIcon size={16} /></button>
            </div>
            <video ref={videoRef} className="w-full rounded-md bg-black" autoPlay playsInline muted />
            <div className="flex gap-2">
              <button onClick={closeCamera} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button onClick={captureFrame} className="flex-1 px-3 py-2 text-sm bg-amber-600 text-white rounded-md flex items-center justify-center gap-1">
                <Camera size={14} /> Capture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read-only bills viewer for detail modals (DocStack OnSiteDoc / GrnDoc)
 * and dashboard drill-downs. Renders each bill with vendor, bill #, date,
 * and a clickable thumbnail (or PDF link). Click thumb → fullscreen
 * lightbox. `compact` shrinks the layout for dashboard rows.
 */
export function BillsViewer({ bills, compact = false }: { bills: any[]; compact?: boolean }) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Track which bill indexes have a broken image fetch (404 from GridFS).
  // Happens when a bill was created but the upload silently failed or the
  // file was later deleted — without this, the modal showed a torn-image
  // icon with no explanation. The `?` placeholder gives the operator a
  // clear "file missing, re-upload from source" signal.
  const [brokenIdx, setBrokenIdx] = useState<Set<number>>(new Set());
  const markBroken = (i: number) => setBrokenIdx((prev) => {
    if (prev.has(i)) return prev;
    const next = new Set(prev);
    next.add(i);
    return next;
  });
  if (!bills?.length) return null;
  const fmtDate = (d: any) => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString();
  };
  return (
    <>
      <div className={compact ? 'flex flex-wrap gap-1.5' : 'space-y-2'}>
        {bills.map((b: any, i: number) => {
          const url = b.attachmentId ? fileUrl(b.attachmentId) : null;
          const isPdf = (b.contentType || '').includes('pdf') || (b.filename || '').toLowerCase().endsWith('.pdf');
          const isBroken = brokenIdx.has(i);
          if (compact) {
            // No file attached → show a muted, non-clickable "no image" chip
            // (a dead link that looks clickable is misleading). The bill's
            // vendor + number still surface via the row text + tooltip.
            if (!url) {
              return (
                <span
                  key={i}
                  className="rounded border border-dashed border-slate-200 px-2 py-1 text-[10px] text-slate-400"
                  title={`Bill #${i + 1}${b.vendorName ? ` · ${b.vendorName}` : ''}${b.vendorBillNo ? ` · ${b.vendorBillNo}` : ''} — no file uploaded`}
                >
                  #{i + 1} · no image
                </span>
              );
            }
            if (isBroken && !isPdf) {
              return (
                <span
                  key={i}
                  className="rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-[10px] text-amber-700"
                  title={`Bill #${i + 1}${b.vendorName ? ` · ${b.vendorName}` : ''}${b.vendorBillNo ? ` · ${b.vendorBillNo}` : ''} — file is no longer in storage (attachmentId ${b.attachmentId})`}
                >
                  #{i + 1} · file missing
                </span>
              );
            }
            return !isPdf ? (
              <button
                key={i}
                type="button"
                onClick={() => setLightboxUrl(url)}
                className="rounded border border-slate-200 hover:border-brand-300 overflow-hidden"
                title={`Bill #${i + 1} · ${b.vendorName || ''} · ${b.vendorBillNo || ''}`}
              >
                <img
                  src={url}
                  alt={`bill-${i + 1}`}
                  className="h-12 w-12 object-cover"
                  onError={() => markBroken(i)}
                />
              </button>
            ) : (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener"
                className="rounded border border-slate-200 hover:border-brand-300 px-2 py-1 text-[10px] font-mono text-brand-700"
                title={b.vendorBillNo || ''}
              >
                📄 #{i + 1}
              </a>
            );
          }
          return (
            <div key={i} className="rounded-md border border-slate-200 bg-slate-50/40 p-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-[10px] text-slate-500">Bill #{i + 1}</span>
                <span className="text-slate-700">{b.vendorName || '—'}</span>
                {b.vendorBillNo && <span className="font-mono text-slate-500">· {b.vendorBillNo}</span>}
                {b.vendorBillDate && <span className="text-slate-500">· {fmtDate(b.vendorBillDate)}</span>}
                {b.filename && <span className="text-slate-400 truncate max-w-[40%]" title={b.filename}>· {b.filename}</span>}
              </div>
              {url && (
                <div className="mt-2">
                  {isPdf ? (
                    <a href={url} target="_blank" rel="noopener" className="text-xs text-brand-700 hover:underline">
                      📄 {b.filename || 'bill.pdf'}
                    </a>
                  ) : isBroken ? (
                    // Image fetch 404'd — file isn't in GridFS. Show an
                    // explicit "file missing" panel rather than the
                    // browser's default torn-image icon so the operator
                    // knows to re-upload from the source.
                    <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-xs">
                      <div className="font-medium mb-0.5">File no longer in storage</div>
                      <div className="text-[11px] text-amber-700/80">
                        Bill metadata is intact, but the uploaded image is missing
                        from server storage. Re-upload the receipt from the source
                        device to restore the preview.
                      </div>
                      <div className="text-[10px] font-mono text-amber-600 mt-1 truncate">
                        attachmentId: {b.attachmentId}
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setLightboxUrl(url)}
                      className="block w-full"
                    >
                      <img
                        src={url}
                        alt={`bill-${i + 1}`}
                        className="max-h-48 w-auto rounded border border-slate-200 object-contain bg-white"
                        onError={() => markBroken(i)}
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
        >
          <img src={lightboxUrl} alt="bill" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </>
  );
}

/**
 * Compact dropdown used on per-cart-row to assign which bill[] index
 * the row belongs to. -1 = unassigned. When `bills` is empty, renders
 * "no bills" hint instead of an empty select.
 */
export function BillRefSelect({
  bills, value, onChange,
}: {
  bills: BillSlot[];
  value: number;
  onChange: (v: number) => void;
}) {
  if (!bills.length) {
    return <span className="text-[10px] text-slate-400 italic">add a bill first</span>;
  }
  return (
    <select
      className="w-full rounded-md border border-slate-300 bg-white px-1.5 py-1 text-xs"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      <option value={-1}>— no bill —</option>
      {bills.map((b, i) => {
        const label = `#${i + 1}${b.vendorName ? ` · ${b.vendorName.slice(0, 16)}` : ''}${b.vendorBillNo ? ` · ${b.vendorBillNo.slice(0, 12)}` : ''}`;
        return <option key={i} value={i}>{label}</option>;
      })}
    </select>
  );
}
